import {
  CheckboxProps,
  RadioGroupProps,
  SelectInputProps,
  SwitchProps,
  TextInputProps,
  DatePickerProps,
  FileUploadProps,
} from '@razorpay/blade/components';
import { FormContextValue } from './types';

type RegisterTextFieldReturnType = Pick<
  TextInputProps,
  'name' | 'onChange' | 'errorText' | 'validationState' | 'value' | 'isDisabled'
>;

type RegisterSelectReturnType = Pick<
  SelectInputProps,
  'name' | 'onChange' | 'errorText' | 'validationState' | 'value' | 'isDisabled'
>;

type RegisterCheckboxReturnType = Pick<
  CheckboxProps,
  'name' | 'onChange' | 'errorText' | 'validationState' | 'isChecked' | 'isDisabled'
>;

type RegisterSwitchReturnType = Pick<SwitchProps, 'name' | 'onChange' | 'isChecked' | 'isDisabled'>;

type RegisterRadioGroupReturnType = Pick<
  RadioGroupProps,
  'name' | 'onChange' | 'errorText' | 'validationState' | 'value' | 'isDisabled'
>;

type RegisterFileUploadReturnType = Pick<
  FileUploadProps,
  'name' | 'onChange' | 'errorText' | 'validationState' | 'isDisabled'
>;

type RegisterDateRangePickerReturnType = Pick<
  DatePickerProps<'range'>,
  'name' | 'onChange' | 'value' | 'isDisabled'
>;

type RegisterDatePickerReturnType = Pick<
  DatePickerProps<'single'>,
  'name' | 'onChange' | 'value' | 'isDisabled'
>;

type FieldType =
  | 'text'
  | 'select'
  | 'checkbox'
  | 'switch'
  | 'radiogroup'
  | 'daterange'
  | 'date'
  | 'fileupload';

export type RegisterGenericType<T> = <K extends FieldType = 'text'>(
  name: keyof T,
  type?: K,
) => K extends 'text'
  ? RegisterTextFieldReturnType
  : K extends 'select'
  ? RegisterSelectReturnType
  : K extends 'checkbox'
  ? RegisterCheckboxReturnType
  : K extends 'switch'
  ? RegisterSwitchReturnType
  : K extends 'radiogroup'
  ? RegisterRadioGroupReturnType
  : K extends 'daterange'
  ? RegisterDateRangePickerReturnType
  : K extends 'date'
  ? RegisterDatePickerReturnType
  : K extends 'fileupload'
  ? RegisterFileUploadReturnType
  : RegisterTextFieldReturnType;

export function useRegister<T>(context: FormContextValue<T>) {
  const { setFieldValue, errors, formData, isSubmitting } = context;

  const register: RegisterGenericType<T> = (name, type = 'text' as FieldType | undefined) => {
    const fieldError = errors[name];
    const value = formData[name];

    switch (type) {
      case 'text':
        return {
          name,
          errorText: fieldError,
          validationState: fieldError ? 'error' : 'none',
          value: value ?? '',
          isDisabled: isSubmitting,
          onChange: (props) => {
            setFieldValue(name, props.value);
          },
        } as RegisterTextFieldReturnType as any;
      case 'select':
        return {
          name,
          errorText: fieldError,
          validationState: fieldError ? 'error' : 'none',
          value: value ?? null,
          isDisabled: isSubmitting,
          onChange: (props) => {
            setFieldValue(name, props.values);
          },
        } as RegisterSelectReturnType as any;
      case 'checkbox':
        return {
          name,
          errorText: fieldError,
          validationState: fieldError ? 'error' : 'none',
          value: value ?? '',
          isDisabled: isSubmitting,
          onChange: (props) => {
            setFieldValue(name, props.isChecked);
          },
        } as RegisterCheckboxReturnType as any;
      case 'switch':
        return {
          name,
          isChecked: value,
          isDisabled: isSubmitting,
          onChange: (props) => {
            setFieldValue(name, props.isChecked);
          },
        } as RegisterSwitchReturnType as any;
      case 'radiogroup':
        return {
          name,
          errorText: fieldError,
          validationState: fieldError ? 'error' : 'none',
          value: value ?? '',
          isDisabled: isSubmitting,
          onChange: (props) => {
            setFieldValue(name, props.value);
          },
        } as RegisterRadioGroupReturnType as any;
      case 'daterange':
        return {
          // name, // Name isn't supported on date range
          errorText: fieldError, // TODO: needs to support date range
          validationState: fieldError ? 'error' : 'none',
          value: value,
          isDisabled: isSubmitting,
          onChange: (range) => {
            setFieldValue(name, range);
          },
        } as RegisterRadioGroupReturnType as any;
      case 'date':
        return {
          name,
          errorText: fieldError, // TODO: needs to support date range
          validationState: fieldError ? 'error' : 'none',
          value: value,
          isDisabled: isSubmitting,
          onChange: (value) => {
            setFieldValue(name, value);
          },
        } as RegisterDatePickerReturnType as any;
      case 'fileupload':
        return {
          name,
          errorText: fieldError,
          validationState: fieldError ? 'error' : 'none',
          value: value,
          isDisabled: isSubmitting,
          onChange: (props) => {
            setFieldValue(name, props.fileList);
          },
        } as RegisterFileUploadReturnType as any;
      default:
        throw new Error('Unsupported field type');
    }
  };

  return { register };
}
